class ProductForm extends HTMLElement {
	constructor() {
		super();

		this.form = this.querySelector('form');
		this.form.querySelector('[name=id]').disabled = false;
		this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
		this.cart =
			document.querySelector('cart-notification') ||
			document.querySelector('cart-drawer') ||
			document.querySelector('ajax-cart');
		this.submitButton = this.querySelector('[type="submit"]');

		if (document.querySelector('cart-drawer'))
			this.submitButton.setAttribute('aria-haspopup', 'dialog');

		this.hideErrors = this.dataset.hideErrors === 'true';

		this.consumeLocation();
	}

	// Buy buttons render disabled until the geolocation service resolves; once it does, restore the server-computed default state stored on the button.
	consumeLocation() {
		// Warranty/upsell scripts (e.g. Mulberry) clone the buy button after construction, so re-resolve it against the live DOM to gate the same button variant-selects toggles rather than a stale/hidden clone.
		this.submitButton = this.querySelector('[name="add"]') || this.submitButton;

		if (!this.submitButton || !this.submitButton.hasAttribute('data-default-disabled')) return;

		if (!window.locationService) {
			this.restoreDefaultButtonState();
			return;
		}

		window.locationService
			.getLocation()
			.then((location) => this.applyRegionAvailability(location))
			.catch(() => this.restoreDefaultButtonState());
	}

	// Externally callable so components like variant-selects can re-evaluate availability after they refresh the inventory script on variant change.
	updateAvailability() {
		this.consumeLocation();
	}

	// Gates the buy button on whether the warehouse serving the visitor's region has stock for the current variant.
	// Missing data or any failure falls back to the server-computed default state.
	applyRegionAvailability(location) {
		try {
			const region = location?.subdivisions?.[0]?.iso_code || window.locationService?.region;
			const inventory = this.getWarehouseInventory();
			const stock = window.RegionalInventory?.getStock(region, inventory) ?? null;

			// Unmapped regions (international or unlisted states) and missing data fall back to the server-computed default state.
			if (stock === null) {
				this.restoreDefaultButtonState();
				return;
			}

			if (stock > 0) {
				this.restoreDefaultButtonState();
			} else {
				this.setUnavailableState();
			}
		} catch (error) {
			console.error('Unable to apply region availability', error);
			this.restoreDefaultButtonState();
		}
	}

	getWarehouseInventory() {
		const script = this.querySelector('[data-warehouse-inventory]');
		if (!script) return null;

		try {
			return JSON.parse(script.textContent);
		} catch (error) {
			console.error('Unable to parse warehouse inventory', error);
			return null;
		}
	}

	setUnavailableState() {
		if (!this.submitButton) return;

		const label = this.submitButton.querySelector('span');
		if (label) label.textContent = window.variantStrings.soldOut;

		this.submitButton.setAttribute('disabled', 'disabled');
		this.submitButton.setAttribute('aria-disabled', 'true');
	}

	restoreDefaultButtonState() {
		if (!this.submitButton) return;

		const defaultDisabled = this.submitButton.dataset.defaultDisabled === 'true';
		const defaultLabel = this.submitButton.dataset.defaultLabel;
		const label = this.submitButton.querySelector('span');

		if (defaultLabel && label) label.textContent = defaultLabel;

		if (defaultDisabled) {
			this.submitButton.setAttribute('disabled', 'disabled');
			this.submitButton.setAttribute('aria-disabled', 'true');
		} else {
			this.submitButton.removeAttribute('disabled');
			this.submitButton.removeAttribute('aria-disabled');
		}
	}

	onSubmitHandler(evt) {
		evt.preventDefault();
		if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
		// Legacy
		if (
			this.submitButton.classList.contains('loading') ||
			this.submitButton.classList.contains('disabled')
		)
			return;

		this.handleErrorMessage();

		this.submitButton.setAttribute('aria-disabled', true);
		this.submitButton.classList.add('loading');
		this.querySelector('.loading__spinner')?.classList.remove('hidden');
		this.querySelector('.loading-overlay__spinner')?.classList.remove('hidden');

		const config = fetchConfig('javascript');
		config.headers['X-Requested-With'] = 'XMLHttpRequest';
		delete config.headers['Content-Type'];

		const formData = new FormData(this.form);
		if (this.cart) {
			formData.append(
				'sections',
				this.cart.getSectionsToRender().map((section) => section.id)
			);
			formData.append('sections_url', window.location.pathname);
			this.cart.setActiveElement(document.activeElement);
		}
		config.body = formData;
		fetch(`${routes.cart_add_url}`, config)
			.then((response) => response.json())
			.then((response) => {
				if (response.status) {
					publish(PUB_SUB_EVENTS.cartError, {
						source: 'product-form',
						productVariantId: formData.get('id'),
						errors: response.errors || response.description,
						message: response.message
					});
					this.handleErrorMessage(response.description);

					const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
					if (!soldOutMessage) return;
					this.submitButton.setAttribute('aria-disabled', true);
					this.submitButton.querySelector('span').classList.add('hidden');
					soldOutMessage.classList.remove('hidden');
					this.error = true;
					return;
				} else if (!this.cart && !p80.cart) {
					window.location = window.routes.cart_url;
					return;
				}

				if (!this.error) {
					publish(PUB_SUB_EVENTS.cartUpdate, {
						source: 'product-form',
						productVariantId: formData.get('id'),
						cartData: response
					});
					this.error = false;
					const quickAddModal = this.closest('quick-add-modal');
					if (quickAddModal) {
						document.body.addEventListener(
							'modalClosed',
							() => {
								setTimeout(() => {
									this.cart.renderContents(response);
								});
							},
							{once: true}
						);
						quickAddModal.hide(true);
					} else {
						if (this.cart && !p80.cart) this.cart.renderContents(response);
					}

					// Swatch Modal A2C
					const modal = this.closest('modal-dialog');
					const swatch = this.closest('.card-wrapper--swatch');
					if (swatch || modal) {
						const button = modal
							? modal.querySelector('.swatch-add')
							: swatch.querySelector('.swatch-add');
						if (button) {
							button.querySelector('span').textContent = window.variantStrings.added;
							button.classList.add('disabled');
						}
						if (modal) modal.hide();
					}

					// Ajax Cart
					if (typeof p80 !== typeof undefined && p80.cart) {
						p80.cart.toggleCart();
					}
				}
			})
			.catch((e) => {
				console.error(e);
			})
			.finally(() => {
				this.submitButton.classList.remove('loading');
				if (this.cart && this.cart.classList.contains('is-empty'))
					this.cart.classList.remove('is-empty');
				if (!this.error) this.submitButton.removeAttribute('aria-disabled');
				this.querySelector('.loading__spinner')?.classList.add('hidden');
				this.querySelector('.loading-overlay__spinner')?.classList.add('hidden');
			});
	}

	handleErrorMessage(errorMessage = false) {
		if (this.hideErrors) return;

		this.errorMessageWrapper =
			this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
		if (!this.errorMessageWrapper) return;
		this.errorMessage =
			this.errorMessage ||
			this.errorMessageWrapper.querySelector('.product-form__error-message');

		this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

		if (errorMessage) {
			this.errorMessage.textContent = errorMessage;
		}
	}
}

if (!customElements.get('product-form')) {
	customElements.define('product-form', ProductForm);
}
